/**
 * DOM quiescence init script.
 *
 * Installs a single page-side MutationObserver whose only job is to tell the
 * live-snapshot manager **when the page has settled** — so the background warm
 * captures at a stable moment rather than mid-render. It is NOT load-bearing for
 * freshness: the manager decides stale-vs-fresh by comparing the raw substrate
 * (`ariaSnapshot`) itself, which is the ground truth (it reflects CSS visibility,
 * shadow DOM, iframes, and layout that a MutationObserver can't see).
 *
 *   - `window.__lastMutationTs` — updated on any DOM mutation OR input/focus
 *     activity, so "quiet" means the page truly stopped changing (typing sets
 *     the `.value` IDL property, which fires no mutation, so we also listen for
 *     input/change/focus to keep the settle signal honest).
 *   - `window.__waitDomQuiet(quietMs, maxWaitMs)` — resolves once nothing has
 *     happened for `quietMs`, bounded by `maxWaitMs` (so streaming pages can't
 *     block). Event-driven, no polling.
 *   - `window.__domRevision` — a best-effort monotonic counter (diagnostics only).
 *   - `window.__lastDialogOpenTs` / `window.__lastDialogName` — stamped whenever a
 *     top-level node matching a dialog/modal role is *added* to the DOM, so the
 *     fast-drive `run_code` wrapper can report "Modal opened" by comparing the
 *     stamp before vs after a block (clock-agnostic — same in-page clock both
 *     reads). Only top-level added nodes are checked (most modal/drawer libraries
 *     portal the overlay to <body>); a deeply-nested non-ARIA drawer won't flag.
 *
 * Installed via `context.addInitScript`, so it runs in the main world of every
 * document. Idempotent — guarded by `__domRevisionInstalled`.
 *
 * @module playwright-client/dom-revision-script
 */

export const DOM_REVISION_SCRIPT = `
(() => {
  if (window.__domRevisionInstalled) return;
  window.__domRevisionInstalled = true;
  window.__domRevision = 0;
  window.__lastDialogOpenTs = 0;
  window.__lastDialogName = '';
  window.__lastMutationTs = (typeof performance !== 'undefined' && performance.now)
    ? performance.now()
    : Date.now();

  var now = function () {
    return (typeof performance !== 'undefined' && performance.now)
      ? performance.now()
      : Date.now();
  };

  var bump = function () {
    window.__domRevision = (window.__domRevision || 0) + 1;
    window.__lastMutationTs = now();
  };

  // Cheap dialog/modal/drawer detection: a single \`.matches\` on each TOP-LEVEL
  // added node (no subtree descent — keeps cost O(addedNodes)). Stamps a
  // timestamp + accessible name the run_code wrapper diffs across a block.
  var DIALOG_SELECTOR = '[role="dialog"],[role="alertdialog"],[aria-modal="true"]';
  var noteDialog = function (node) {
    try {
      if (!node || node.nodeType !== 1 || typeof node.matches !== 'function') return;
      if (!node.matches(DIALOG_SELECTOR)) return;
      window.__lastDialogOpenTs = now();
      // Name from O(1) attributes only — never node.textContent, which would
      // force a synchronous full-subtree text walk inside the observer callback
      // (this script runs in EVERY session, so the dialog scan must stay cheap).
      var name = node.getAttribute('aria-label') || node.getAttribute('title') || '';
      window.__lastDialogName = name.replace(/\\s+/g, ' ').trim().slice(0, 80);
    } catch (e) {}
  };

  var onMutations = function (mutations) {
    bump();
    try {
      for (var i = 0; i < mutations.length; i++) {
        var added = mutations[i].addedNodes;
        if (!added) continue;
        for (var j = 0; j < added.length; j++) noteDialog(added[j]);
      }
    } catch (e) {}
  };

  var install = function () {
    try {
      // Observe the Document node itself (not documentElement): it survives
      // document.open()/write() — which replaces documentElement and would
      // orphan an observer attached to it — while subtree:true still catches
      // every descendant childList/attribute/text mutation.
      var observer = new MutationObserver(onMutations);
      observer.observe(document, {
        childList: true,
        attributes: true,
        characterData: true,
        subtree: true,
      });
      window.__domRevisionObserver = observer;
      return true;
    } catch (e) {
      return false;
    }
  };

  if (!install()) {
    document.addEventListener('DOMContentLoaded', install, { once: true });
  }

  // Input/focus activity doesn't always fire a mutation (typing sets the .value
  // property), but it IS "the page is changing" for settle purposes — so count
  // it toward __lastMutationTs. Capture phase on window so it survives document
  // replacement and catches events from inside shadow DOM (they're composed).
  window.addEventListener('input', bump, true);
  window.addEventListener('change', bump, true);
  window.addEventListener('focusin', bump, true);
  window.addEventListener('focusout', bump, true);

  // Resolves when the DOM has been quiet (no mutations / input) for quietMs, or
  // after maxWaitMs elapses — whichever comes first. Event-driven (no polling):
  // a single timer is (re)scheduled off __lastMutationTs.
  window.__waitDomQuiet = function (quietMs, maxWaitMs) {
    quietMs = typeof quietMs === 'number' ? quietMs : 100;
    maxWaitMs = typeof maxWaitMs === 'number' ? maxWaitMs : 2000;
    return new Promise(function (resolve) {
      var start = now();
      var timer = null;
      var done = false;

      var finish = function (reason) {
        if (done) return;
        done = true;
        if (timer) clearTimeout(timer);
        resolve(reason);
      };

      var schedule = function () {
        if (done) return;
        var sinceMutation = now() - (window.__lastMutationTs || 0);
        var sinceStart = now() - start;
        // Hard cap: never block past maxWaitMs (streaming/websocket pages).
        if (sinceStart >= maxWaitMs) {
          finish('maxwait');
          return;
        }
        if (sinceMutation >= quietMs) {
          finish('quiet');
          return;
        }
        // Wait for the shorter of "quiet window remaining" / "maxwait remaining",
        // then re-check (a mutation may have landed since, moving __lastMutationTs).
        var waitForQuiet = quietMs - sinceMutation;
        var waitForCap = maxWaitMs - sinceStart;
        timer = setTimeout(schedule, Math.max(0, Math.min(waitForQuiet, waitForCap)));
      };

      schedule();
    });
  };
})();
`;
